/**
 * Vendor ROI Tracker Page (Issue #1160, Fixed #1173)
 *
 * Page component for vendor ROI tracking with revenue and expense management.
 * Combines RevenueTracker, ExpenseTracker, and FairROISummary components.
 *
 * NOTE: This page is already protected by VendorAuthGuard at the route level,
 * so no additional authentication check is needed here.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { RevenueTracker } from '../components/RevenueTracker';
import { ExpenseTracker } from '../components/ExpenseTracker';
import { FairROISummary } from '../components/FairROISummary';
import { FairSelector } from '../components/FairSelector';

const VendorROITrackerPage: React.FC = () => {
  const { fairId: fairIdParam } = useParams<{ fairId: string }>();
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authApi.getCurrentUser();
        setVendorId(user.id);
      } catch (error) {
        console.error('Failed to load user:', error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const handleDataChange = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </div>
    );
  }

  if (!vendorId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          Failed to load user information. Please try refreshing the page.
        </div>
      </div>
    );
  }

  const fairId = fairIdParam ? parseInt(fairIdParam, 10) : null;

  if (!fairId || isNaN(fairId) || fairId <= 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          Invalid fair ID. Please check the URL and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">ROI Tracker</h1>
          <FairSelector vendorId={vendorId} currentFairId={fairId} basePath="roi-tracker" />
        </div>

        {/* ROI Summary */}
        <FairROISummary vendorId={vendorId} fairId={fairId} refreshTrigger={refreshTrigger} />

        {/* Revenue and Expense Trackers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ExpenseTracker vendorId={vendorId} fairId={fairId} onExpenseAdded={handleDataChange} />
          <RevenueTracker vendorId={vendorId} fairId={fairId} onRevenueAdded={handleDataChange} />
        </div>
      </div>
    </div>
  );
};

export default VendorROITrackerPage;
